// The organization's Integrations tab (M7 slice 3; spec/dashboard.md section 12.4; spec/api-conventions.md section 21.3;
// `org_manage`): Google Sheets — the service account's key file pasted into a textarea (`PUT …/integrations/google-sheets`),
// the status with the address to share a sheet with; SMTP — host, port, username, the write-only password ("leave empty
// to keep"), from, security (`PUT …/integrations/smtp`), Send test mail (`POST …/smtp:test`, to the caller). Each has
// Remove (confirm). The secrets never come back: the status shows what the API answers and nothing else.
import { useEffect, useState, type FormEvent } from 'react';
import { ops } from '../../api/operations';
import type { GoogleSheetsIntegration, SmtpIntegration } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { Checkbox, Select, TextArea, TextInput } from '../../ui/inputs';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError } from '../settings/keys';
import type { OrgAccess } from './orgAccess';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { AlertCircle, CheckCircle2, Copy, Lock, Mail, Table2 } from '../../ui/icons';

export const integrationsKey = (org: string) => `org:${org}:integrations`;

export function CopyText({ value, label }: { value: string; label: string }) {
  const toasts = useToasts();
  return (
    <Button
      size="s"
      icon={<Copy aria-hidden="true" />}
      aria-label={`Copy ${label}`}
      onClick={() => {
        navigator.clipboard?.writeText(value).then(
          () => toasts.success(`${label} copied.`),
          () => toasts.error('Could not copy; select the text and copy it by hand.'),
        );
      }}
    >
      Copy
    </Button>
  );
}

function Updated({ at, by, when }: { at: string | null; by: { email: string; displayName: string } | null; when: (v: string | null | undefined) => string }) {
  if (!at) return null;
  return (
    <p className="muted small" data-testid="integration-updated">
      Updated {when(at)}
      {by ? ` by ${by.displayName || by.email}` : ''}.
    </p>
  );
}

export function OrgIntegrations({ org, access, when }: { org: string; access: OrgAccess; when: (v: string | null | undefined) => string }) {
  const { client } = useAuth();
  const key = integrationsKey(org);
  const q = useQuery(access.manages ? key : null, () => client.data(ops.getIntegrations, { path: { org } }));
  if (!access.manages) {
    return (
      <div data-testid="org-integrations">
        {access.loading ? (
          <LoadingPane rows={3} />
        ) : (
          <div className="pane">
            <div className="pane-body">
              <p className="muted settings-locked" role="note">
                <Lock className="icon-s" aria-hidden="true" /> You lack the 'org_manage' permission: the organization's integrations are managed by its owners and admins.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
  const data = q.data;
  return (
    <div data-testid="org-integrations" className="org-integrations">
      {q.error ? (
        <LoadError>The integrations could not be read.</LoadError>
      ) : null}
      {!data ? (
        q.loading ? <LoadingPane rows={3} /> : null
      ) : (
        <>
          <GoogleSheetsCard org={org} status={data.googleSheets} when={when} onChanged={() => queryCache.invalidate(key)} />
          <SmtpCard org={org} status={data.smtp} when={when} onChanged={() => queryCache.invalidate(key)} />
        </>
      )}
    </div>
  );
}

function GoogleSheetsCard({ org, status, when, onChanged }: { org: string; status: GoogleSheetsIntegration; when: (v: string | null | undefined) => string; onChanged: () => void }) {
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [json, setJson] = useState('');
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('save');
    setError(null);
    try {
      await client.data(ops.setGoogleSheetsIntegration, { path: { org }, body: { serviceAccountJson: json.trim() } });
      setJson('');
      toasts.success('The Google service account was saved.');
      onChanged();
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: 'Remove the Google service account?', body: 'Imports and exports from Google Sheets need an access token per request again.', confirmLabel: 'Remove', tone: 'danger' }))) return;
    setBusy('remove');
    try {
      await client.call(ops.deleteGoogleSheetsIntegration, { path: { org } });
      toasts.success('The Google service account was removed.');
      onChanged();
    } catch (err) {
      const api = asApiError(err);
      toasts.error(api.message, { requestId: api.requestId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="pane settings-form" data-testid="integration-google-sheets" aria-label="Google Sheets">
      <div className="pane-head">
        <Table2 className="icon" aria-hidden="true" />
        <h2>Google Sheets</h2>
        <div className="spacer" />
        {status.configured ? <Badge tone="success">configured</Badge> : <Badge>not configured</Badge>}
      </div>
      <div className="pane-body">
      {status.configured ? (
        <>
          <dl className="kv">
            <dt>Service account</dt>
            <dd className="row">
              <code data-testid="sheets-client-email">{status.clientEmail}</code>
              {status.clientEmail && <CopyText value={status.clientEmail} label="Service account address" />}
            </dd>
            {status.projectId && (
              <>
                <dt>Project</dt>
                <dd>
                  <code>{status.projectId}</code>
                </dd>
              </>
            )}
            {status.keyHint && (
              <>
                <dt>Key</dt>
                <dd>
                  <code>…{status.keyHint}</code>
                </dd>
              </>
            )}
          </dl>
          <p className="small" role="note">
            Share the sheet with this address (Editor for exports, Viewer is enough for imports); imports and exports then need no access token.
          </p>
          <Updated at={status.updatedAt} by={status.updatedBy} when={when} />
        </>
      ) : (
        <p className="muted small">Without a service account, every import or export from Google Sheets asks for an access token.</p>
      )}
      </div>
      <form onSubmit={(e) => void save(e)} aria-label="Google service account">
        <div className="pane-body integration-form">
        <Field label={status.configured ? 'Replace the key file (JSON)' : 'Service account key file (JSON)'} help="The JSON key file of a Google service account; stored sealed, never shown again." error={error?.fieldMessage('serviceAccountJson')}>
          <TextArea aria-label="Service account key file" rows={6} value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false} placeholder='{ "type": "service_account", … }' />
        </Field>
        {error && !error.details?.length && (
          <LoadError>{error.message}</LoadError>
        )}
        </div>
        <div className="pane-foot">
          <Button type="submit" variant="primary" busy={busy === 'save'} disabled={!json.trim()}>
            Save
          </Button>
          <div className="spacer" />
          {status.configured && (
            <Button variant="danger" onClick={() => void remove()} busy={busy === 'remove'}>
              Remove…
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}

const SECURITY = ['starttls', 'tls', 'none'] as const;

function SmtpCard({ org, status, when, onChanged }: { org: string; status: SmtpIntegration; when: (v: string | null | undefined) => string; onChanged: () => void }) {
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [from, setFrom] = useState('');
  const [security, setSecurity] = useState('starttls');
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState<'save' | 'remove' | 'test' | null>(null);
  const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setHost(status.host ?? '');
    setPort(status.port !== null && status.port !== undefined ? String(status.port) : '587');
    setUsername(status.username ?? '');
    setFrom(status.from ?? '');
    setSecurity(status.security ?? 'starttls');
    setPassword('');
    setClearPassword(false);
  }, [status]);

  const portValid = /^\d+$/.test(port.trim()) && Number(port) >= 1 && Number(port) <= 65535;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!portValid) return;
    setBusy('save');
    setError(null);
    try {
      const body: { host: string; port: number; username: string | null; from: string; security: string; password?: string | null } = { host: host.trim(), port: Number(port), username: username.trim() || null, from: from.trim(), security };
      if (password) body.password = password;
      else if (clearPassword) body.password = null;
      await client.data(ops.setSmtpIntegration, { path: { org }, body });
      toasts.success('The SMTP settings were saved.');
      onChanged();
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: 'Remove the SMTP settings?', body: 'Invites are no longer mailed and password reset by email stops working for this organization.', confirmLabel: 'Remove', tone: 'danger' }))) return;
    setBusy('remove');
    try {
      await client.call(ops.deleteSmtpIntegration, { path: { org } });
      toasts.success('The SMTP settings were removed.');
      setTest(null);
      onChanged();
    } catch (err) {
      const api = asApiError(err);
      toasts.error(api.message, { requestId: api.requestId });
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    setBusy('test');
    setTest(null);
    try {
      const r = await client.data(ops.testSmtpIntegration, { path: { org } });
      setTest({ ok: r.sent, text: r.sent ? `Test mail sent to ${r.to}.` : `The test mail to ${r.to} was not sent.` });
    } catch (err) {
      const api = asApiError(err);
      setTest({ ok: false, text: `The test mail failed: ${api.fieldMessage('smtp') ?? api.message}` });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="pane settings-form" data-testid="integration-smtp" aria-label="SMTP">
      <div className="pane-head">
        <Mail className="icon" aria-hidden="true" />
        <h2>SMTP</h2>
        <div className="spacer" />
        {status.configured ? <Badge tone="success">configured</Badge> : <Badge>not configured</Badge>}
      </div>
      <form onSubmit={(e) => void save(e)} aria-label="SMTP settings">
        <div className="pane-body">
        <p className="muted small">With SMTP the organization's invites are mailed as well as shown, and its members can reset a forgotten password by email.</p>
        {status.configured && <Updated at={status.updatedAt} by={status.updatedBy} when={when} />}
        <div className="form-grid">
        <Field label="Host" required error={error?.fieldMessage('host')}>
          <TextInput aria-label="Host" value={host} onChange={(e) => setHost(e.target.value)} required autoComplete="off" placeholder="smtp.example.com" />
        </Field>
        <Field label="Port" required error={error?.fieldMessage('port') ?? (portValid ? undefined : 'A port from 1 to 65535.')}>
          <TextInput type="number" aria-label="Port" value={port} onChange={(e) => setPort(e.target.value)} min={1} max={65535} />
        </Field>
        <Field label="Username" error={error?.fieldMessage('username')}>
          <TextInput aria-label="Username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Password" help={status.hasPassword ? 'A password is stored; leave empty to keep it.' : 'Write-only: never shown again.'} error={error?.fieldMessage('password')}>
          <TextInput type="password" aria-label="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder={status.hasPassword ? 'leave empty to keep' : ''} />
        </Field>
        {status.hasPassword && !password && (
          <div className="field integration-check">
            <Checkbox label="Remove the stored password" checked={clearPassword} onChange={(e) => setClearPassword(e.target.checked)} />
          </div>
        )}
        <Field label="From" required help="An address, optionally as Name <address>." error={error?.fieldMessage('from')}>
          <TextInput aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} required autoComplete="off" />
        </Field>
        <Field label="Security" error={error?.fieldMessage('security')}>
          <Select aria-label="Security" value={security} onChange={(e) => setSecurity(e.target.value)}>
            {SECURITY.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        </div>
        {error && !error.details?.length && (
          <LoadError>{error.message}</LoadError>
        )}
        {test && (
          <div className={`banner ${test.ok ? 'banner-success' : 'banner-danger'}`} role="status" data-testid="smtp-test-result">
            {test.ok ? <CheckCircle2 className="banner-icon" aria-hidden="true" /> : <AlertCircle className="banner-icon" aria-hidden="true" />}
            <div className="banner-body">{test.text}</div>
          </div>
        )}
        </div>
        <div className="pane-foot">
          <Button type="submit" variant="primary" busy={busy === 'save'} disabled={!host.trim() || !from.trim() || !portValid}>
            Save
          </Button>
          {status.configured && (
            <Button icon={<Mail aria-hidden="true" />} onClick={() => void sendTest()} busy={busy === 'test'}>
              Send test mail
            </Button>
          )}
          <div className="spacer" />
          {status.configured && (
            <Button variant="danger" onClick={() => void remove()} busy={busy === 'remove'}>
              Remove…
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}

